import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Clock, MapPin, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { LOCAIS_PROVA, RESULTADOS_PROVA, ProvaAgendada } from "@/constants/prova-agendamento";

interface ProvaUnificadoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (prova: ProvaAgendada) => Promise<void>;
  clienteNome?: string;
  pedidoNumero?: string;
  provaExistente?: ProvaAgendada | null;
}

export function ProvaUnificadoModal({
  isOpen,
  onClose,
  onConfirm,
  clienteNome = "Cliente",
  pedidoNumero = "",
  provaExistente = null,
}: ProvaUnificadoModalProps) {
  const [activeTab, setActiveTab] = useState<"agendar" | "registrar" | "editar">("agendar");
  const [isLoading, setIsLoading] = useState(false);

  // Agendar
  const [data, setData] = useState("");
  const [hora, setHora] = useState("");
  const [local, setLocal] = useState<"atelier" | "cliente" | "video">("atelier");
  const [obsAgendamento, setObsAgendamento] = useState("");

  // Registrar Resultado
  const [dataRealizacao, setDataRealizacao] = useState("");
  const [horaRealizacao, setHoraRealizacao] = useState("");
  const [resultado, setResultado] = useState<"aprovada" | "ajustes_necessarios" | "nao_realizada">("aprovada");
  const [obsResultado, setObsResultado] = useState("");

  // Editar
  const [dataEdicao, setDataEdicao] = useState("");
  const [horaEdicao, setHoraEdicao] = useState("");
  const [localEdicao, setLocalEdicao] = useState<"atelier" | "cliente" | "video">("atelier");
  const [obsEdicaoAgendamento, setObsEdicaoAgendamento] = useState("");

  // Inicializar valores se existir prova
  useEffect(() => {
    if (provaExistente) {
      setDataEdicao(provaExistente.data_prova);
      setHoraEdicao(provaExistente.hora_prova);
      setLocalEdicao(provaExistente.local_prova);
      setObsEdicaoAgendamento(provaExistente.observacoes_agendamento || "");
    }
  }, [provaExistente, isOpen]);

  const handleConfirmAgendar = async () => {
    if (!data || !hora) {
      toast.error("Preencha data e hora da prova");
      return;
    }

    setIsLoading(true);
    try {
      const prova: ProvaAgendada = {
        id: provaExistente?.id || `prova_${Date.now()}`,
        data_prova: data,
        hora_prova: hora,
        local_prova: local,
        observacoes_agendamento: obsAgendamento || undefined,
        confirmada: false,
        data_agendamento: provaExistente?.data_agendamento || new Date().toISOString(),
        data_atualizacao: new Date().toISOString(),
      };

      await onConfirm(prova);
      resetForm();
      onClose();
    } catch (error) {
      console.error("Erro ao agendar prova:", error);
      toast.error("Erro ao agendar prova");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmRegistrar = async () => {
    console.log("📋 [handleConfirmRegistrar] Iniciando...", { dataRealizacao, horaRealizacao });

    if (!dataRealizacao || !horaRealizacao) {
      console.warn("⚠️ [handleConfirmRegistrar] Dados faltando", { dataRealizacao, horaRealizacao });
      toast.error("Preencha data e hora da prova realizada");
      return;
    }

    setIsLoading(true);
    try {
      console.log("🔨 [handleConfirmRegistrar] Criando objeto prova...");
      const prova: ProvaAgendada = {
        id: provaExistente?.id || `prova_${Date.now()}`,
        data_prova: provaExistente?.data_prova || dataRealizacao,
        hora_prova: provaExistente?.hora_prova || horaRealizacao,
        local_prova: provaExistente?.local_prova || "atelier",
        observacoes_agendamento: provaExistente?.observacoes_agendamento,
        realizada: true,
        data_realizacao: dataRealizacao,
        hora_realizacao: horaRealizacao,
        resultado_prova: resultado,
        observacoes_resultado: obsResultado || undefined,
        confirmada: provaExistente?.confirmada || false,
        data_agendamento: provaExistente?.data_agendamento || new Date().toISOString(),
        data_atualizacao: new Date().toISOString(),
      };
      console.log("✅ [handleConfirmRegistrar] Objeto criado:", prova);

      console.log("📤 [handleConfirmRegistrar] Chamando onConfirm...");
      await onConfirm(prova);
      console.log("✅ [handleConfirmRegistrar] onConfirm concluído");

      resetForm();
      onClose();
      console.log("✅ [handleConfirmRegistrar] Modal fechado");
    } catch (error) {
      console.error("❌ [handleConfirmRegistrar] Erro:", error);
      toast.error("Erro ao registrar prova");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmEditar = async () => {
    if (!dataEdicao || !horaEdicao || !provaExistente) {
      toast.error("Preencha os dados");
      return;
    }

    setIsLoading(true);
    try {
      const prova: ProvaAgendada = {
        ...provaExistente,
        data_prova: dataEdicao,
        hora_prova: horaEdicao,
        local_prova: localEdicao,
        observacoes_agendamento: obsEdicaoAgendamento || undefined,
        data_atualizacao: new Date().toISOString(),
      };

      await onConfirm(prova);
      resetForm();
      onClose();
    } catch (error) {
      console.error("Erro ao editar prova:", error);
      toast.error("Erro ao editar prova");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setData("");
    setHora("");
    setLocal("atelier");
    setObsAgendamento("");
    setDataRealizacao("");
    setHoraRealizacao("");
    setResultado("aprovada");
    setObsResultado("");
  };

  const localInfo = LOCAIS_PROVA.find((l) => l.value === local);
  const localInfoEdicao = LOCAIS_PROVA.find((l) => l.value === localEdicao);
  const resultadoInfo = RESULTADOS_PROVA.find((r) => r.value === resultado);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>📅</span>
            {provaExistente ? "Editar Prova" : "Gerenciar Prova"}
          </DialogTitle>
          <DialogDescription>
            Pedido #{pedidoNumero} - {clienteNome}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="agendar">📅 Agendar</TabsTrigger>
            <TabsTrigger value="registrar">✓ Registrar</TabsTrigger>
            {provaExistente && <TabsTrigger value="editar">✏️ Editar</TabsTrigger>}
          </TabsList>

          {/* ABA 1: AGENDAR PROVA */}
          <TabsContent value="agendar" className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="data-prova" className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Data da Prova
              </Label>
              <Input
                id="data-prova"
                type="date"
                value={data}
                onChange={(e) => setData(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hora-prova" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Hora da Prova
              </Label>
              <Input
                id="hora-prova"
                type="time"
                value={hora}
                onChange={(e) => setHora(e.target.value)}
              />
            </div>

            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Local da Prova
              </Label>
              <div className="space-y-2">
                {LOCAIS_PROVA.map((loc) => (
                  <Card
                    key={loc.value}
                    className={`p-3 cursor-pointer transition ${
                      local === loc.value
                        ? "bg-purple-50 border-purple-300 border-2"
                        : "bg-white hover:bg-gray-50"
                    }`}
                    onClick={() => setLocal(loc.value as any)}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="local"
                        value={loc.value}
                        checked={local === loc.value}
                        onChange={() => setLocal(loc.value as any)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{loc.label}</p>
                        <p className="text-xs text-gray-500">{loc.description}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="obs-agendamento">Observações (Opcional)</Label>
              <Textarea
                id="obs-agendamento"
                placeholder="Ex: Trazer referência de cor, medir novamente, etc."
                value={obsAgendamento}
                onChange={(e) => setObsAgendamento(e.target.value)}
                rows={3}
                className="text-sm"
              />
            </div>

            {data && hora && localInfo && (
              <Card className="p-3 bg-purple-50 border-purple-200">
                <p className="text-sm text-purple-800">
                  <span className="font-semibold">✓ Prova agendada para:</span> {data} às {hora} - {localInfo.label}
                </p>
              </Card>
            )}
          </TabsContent>

          {/* ABA 2: REGISTRAR RESULTADO */}
          <TabsContent value="registrar" className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>ℹ️ Registre aqui quando a prova foi realizada</strong>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="data-realizacao" className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Data da Prova (Realizada)
              </Label>
              <Input
                id="data-realizacao"
                type="date"
                value={dataRealizacao}
                onChange={(e) => setDataRealizacao(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hora-realizacao" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Hora da Prova
              </Label>
              <Input
                id="hora-realizacao"
                type="time"
                value={horaRealizacao}
                onChange={(e) => setHoraRealizacao(e.target.value)}
              />
            </div>

            <div className="space-y-3">
              <Label>Resultado da Prova</Label>
              <div className="space-y-2">
                {RESULTADOS_PROVA.map((res) => (
                  <Card
                    key={res.value}
                    className={`p-3 cursor-pointer transition ${
                      resultado === res.value
                        ? `bg-${res.color}-50 border-${res.color}-300 border-2`
                        : "bg-white hover:bg-gray-50"
                    }`}
                    onClick={() => setResultado(res.value as any)}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="resultado"
                        value={res.value}
                        checked={resultado === res.value}
                        onChange={() => setResultado(res.value as any)}
                      />
                      <p className="font-medium text-sm">{res.label}</p>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="obs-resultado">Observações sobre a Prova (Opcional)</Label>
              <Textarea
                id="obs-resultado"
                placeholder="Ex: Cliente aprovou, precisa ajustar comprimento, etc."
                value={obsResultado}
                onChange={(e) => setObsResultado(e.target.value)}
                rows={3}
                className="text-sm"
              />
            </div>

            {dataRealizacao && horaRealizacao && resultadoInfo && (
              <Card className="p-3 bg-green-50 border-green-200">
                <p className="text-sm text-green-800">
                  <span className="font-semibold">✓ Prova registrada:</span> {dataRealizacao} às {horaRealizacao} - {resultadoInfo.label}
                </p>
              </Card>
            )}
          </TabsContent>

          {/* ABA 3: EDITAR AGENDAMENTO */}
          {provaExistente && (
            <TabsContent value="editar" className="space-y-4 py-4">
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>⚠️ Edite os dados do agendamento da prova</strong>
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="data-edicao" className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Data da Prova
                </Label>
                <Input
                  id="data-edicao"
                  type="date"
                  value={dataEdicao}
                  onChange={(e) => setDataEdicao(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="hora-edicao" className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Hora da Prova
                </Label>
                <Input
                  id="hora-edicao"
                  type="time"
                  value={horaEdicao}
                  onChange={(e) => setHoraEdicao(e.target.value)}
                />
              </div>

              <div className="space-y-3">
                <Label>Local da Prova</Label>
                <div className="space-y-2">
                  {LOCAIS_PROVA.map((loc) => (
                    <Card
                      key={loc.value}
                      className={`p-3 cursor-pointer transition ${
                        localEdicao === loc.value
                          ? "bg-purple-50 border-purple-300 border-2"
                          : "bg-white hover:bg-gray-50"
                      }`}
                      onClick={() => setLocalEdicao(loc.value as any)}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="radio"
                          name="local-edicao"
                          value={loc.value}
                          checked={localEdicao === loc.value}
                          onChange={() => setLocalEdicao(loc.value as any)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{loc.label}</p>
                          <p className="text-xs text-gray-500">{loc.description}</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="obs-edicao">Observações (Opcional)</Label>
                <Textarea
                  id="obs-edicao"
                  placeholder="Ex: Trazer referência de cor, medir novamente, etc."
                  value={obsEdicaoAgendamento}
                  onChange={(e) => setObsEdicaoAgendamento(e.target.value)}
                  rows={3}
                  className="text-sm"
                />
              </div>

              {dataEdicao && horaEdicao && localInfoEdicao && (
                <Card className="p-3 bg-blue-50 border-blue-200">
                  <p className="text-sm text-blue-800">
                    <span className="font-semibold">✓ Prova atualizada para:</span> {dataEdicao} às {horaEdicao} - {localInfoEdicao.label}
                  </p>
                </Card>
              )}
            </TabsContent>
          )}
        </Tabs>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          {activeTab === "agendar" && (
            <Button
              onClick={handleConfirmAgendar}
              disabled={isLoading || !data || !hora}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Agendar Prova
            </Button>
          )}
          {activeTab === "registrar" && (
            <Button
              onClick={handleConfirmRegistrar}
              disabled={isLoading || !dataRealizacao || !horaRealizacao}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Registrar Prova
            </Button>
          )}
          {activeTab === "editar" && (
            <Button
              onClick={handleConfirmEditar}
              disabled={isLoading || !dataEdicao || !horaEdicao}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salvar Alterações
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
